import { create } from 'zustand'
import type {
  PreferenceDefaults,
  PreferenceLayoutNode,
  PreferenceTab,
  UserPreferences,
} from '../models'
import type { PreferenceGroupDetail } from '../api/contracts'
import { getAllPreferences } from '../logic/preferences'

export interface PreferenceEditSlot<T> {
  saved: T | undefined
  draft: T | undefined
}

interface PreferenceEditingState {
  preferenceDefaults: PreferenceEditSlot<PreferenceDefaults>
  preferenceGroups: Record<string, PreferenceEditSlot<PreferenceGroupDetail>>
  userPreferences: PreferenceEditSlot<UserPreferences>
  hydratePreferenceDefaults: (defaults: PreferenceDefaults) => void
  updatePreferenceDefaultsDraft: (
    updater: (current: PreferenceDefaults | undefined) => PreferenceDefaults | undefined
  ) => void
  commitPreferenceDefaults: (
    defaults: PreferenceDefaults,
    submittedDraft?: PreferenceDefaults
  ) => void
  replacePreferenceDefaultsSlot: (slot: PreferenceEditSlot<PreferenceDefaults>) => void
  discardPreferenceDefaults: () => void
  hydratePreferenceGroup: (preferenceGroupId: string, group: PreferenceGroupDetail) => void
  updatePreferenceGroupDraft: (
    preferenceGroupId: string,
    updater: (current: PreferenceGroupDetail | undefined) => PreferenceGroupDetail | undefined
  ) => void
  commitPreferenceGroup: (
    preferenceGroupId: string,
    group: PreferenceGroupDetail,
    submittedDraft?: PreferenceGroupDetail
  ) => void
  replacePreferenceGroupSlot: (
    preferenceGroupId: string,
    slot: PreferenceEditSlot<PreferenceGroupDetail>
  ) => void
  discardPreferenceGroup: (preferenceGroupId: string) => void
  hydrateUserPreferences: (preferences: UserPreferences) => void
  updateUserPreferencesDraft: (
    updater: (current: UserPreferences | undefined) => UserPreferences | undefined
  ) => void
  commitUserPreferences: (preferences: UserPreferences, submittedDraft?: UserPreferences) => void
  replaceUserPreferencesSlot: (slot: PreferenceEditSlot<UserPreferences>) => void
  discardUserPreferences: () => void
  resetPreferenceEditing: () => void
}

function cloneSnapshot<T>(snapshot: T | undefined): T | undefined {
  if (snapshot === undefined) return undefined
  return JSON.parse(JSON.stringify(snapshot)) as T
}

function snapshotsEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function slotHasChanges<T>(slot: PreferenceEditSlot<T>) {
  return Boolean(
    slot.saved !== undefined && slot.draft !== undefined && !snapshotsEqual(slot.saved, slot.draft)
  )
}

export function preferenceEditSlotHasChanges<T>(slot: PreferenceEditSlot<T>) {
  return slotHasChanges(slot)
}

function emptySlot<T>(): PreferenceEditSlot<T> {
  return {
    saved: undefined,
    draft: undefined,
  }
}

type PreferenceSnapshot = { tabs: PreferenceTab[] }

/**
 * The user's own edits (what the draft changed from the copy it started from)
 * replayed onto what is saved now. A preference saved elsewhere meanwhile (the
 * header's Theme, the sidebar, another tab) keeps its new value instead of the
 * draft's stale copy of it, which Save would otherwise send back.
 */
function rebaseDraft<T extends PreferenceSnapshot>(base: T, draft: T, next: T): T {
  const rebased = cloneSnapshot(next) as T
  // Fields beside the preferences, such as a group's name or members.
  for (const key of Object.keys(draft) as (keyof T)[]) {
    if (key === 'tabs' || snapshotsEqual(draft[key], base[key])) continue
    rebased[key] = cloneSnapshot(draft[key]) as T[keyof T]
  }

  const baseById = new Map(
    getAllPreferences(base.tabs).map((preference) => [preference.preference_id, preference])
  )
  const editedById = new Map(
    getAllPreferences(draft.tabs)
      .filter((preference) => !snapshotsEqual(preference, baseById.get(preference.preference_id)))
      .map((preference) => [preference.preference_id, preference])
  )
  if (editedById.size === 0) return rebased

  const withEdits = (node: PreferenceLayoutNode): PreferenceLayoutNode => ({
    ...node,
    preferences: node.preferences.map(
      (preference) => cloneSnapshot(editedById.get(preference.preference_id)) ?? preference
    ),
    children: node.children.map(withEdits),
  })
  rebased.tabs = rebased.tabs.map((tab) => ({
    ...tab,
    layout_nodes: tab.layout_nodes.map(withEdits),
  })) as T['tabs']
  return rebased
}

function hydrateSlot<T extends PreferenceSnapshot>(
  slot: PreferenceEditSlot<T>,
  saved: T
): PreferenceEditSlot<T> {
  const nextSaved = cloneSnapshot(saved)

  return {
    saved: nextSaved,
    draft:
      slotHasChanges(slot) && slot.saved && slot.draft && nextSaved
        ? rebaseDraft(slot.saved, slot.draft, nextSaved)
        : cloneSnapshot(nextSaved),
  }
}

function committedSlot<T>(saved: T): PreferenceEditSlot<T> {
  const nextSaved = cloneSnapshot(saved)

  return {
    saved: nextSaved,
    draft: cloneSnapshot(nextSaved),
  }
}

function replacedSlot<T>(slot: PreferenceEditSlot<T>): PreferenceEditSlot<T> {
  return {
    saved: cloneSnapshot(slot.saved),
    draft: cloneSnapshot(slot.draft),
  }
}

function updateDraftSlot<T>(
  slot: PreferenceEditSlot<T>,
  updater: (current: T | undefined) => T | undefined
): PreferenceEditSlot<T> {
  const current = slot.draft ?? slot.saved
  return {
    ...slot,
    draft: cloneSnapshot(updater(current)),
  }
}

function discardedSlot<T>(slot: PreferenceEditSlot<T>): PreferenceEditSlot<T> {
  return {
    ...slot,
    draft: cloneSnapshot(slot.saved),
  }
}

export const usePreferenceEditingStore = create<PreferenceEditingState>()((set) => ({
  preferenceDefaults: emptySlot(),
  preferenceGroups: {},
  userPreferences: emptySlot(),

  hydratePreferenceDefaults: (defaults) =>
    set((state) => ({
      preferenceDefaults: hydrateSlot(state.preferenceDefaults, defaults),
    })),
  updatePreferenceDefaultsDraft: (updater) =>
    set((state) => ({
      preferenceDefaults: updateDraftSlot(state.preferenceDefaults, updater),
    })),
  commitPreferenceDefaults: (defaults, submittedDraft) =>
    set((state) => ({
      preferenceDefaults:
        submittedDraft && state.preferenceDefaults.draft
          ? {
              saved: cloneSnapshot(defaults),
              draft: rebaseDraft(submittedDraft, state.preferenceDefaults.draft, defaults),
            }
          : committedSlot(defaults),
    })),
  replacePreferenceDefaultsSlot: (slot) => set({ preferenceDefaults: replacedSlot(slot) }),
  discardPreferenceDefaults: () =>
    set((state) => ({
      preferenceDefaults: discardedSlot(state.preferenceDefaults),
    })),

  hydratePreferenceGroup: (preferenceGroupId, group) =>
    set((state) => ({
      preferenceGroups: {
        ...state.preferenceGroups,
        [preferenceGroupId]: hydrateSlot(
          state.preferenceGroups[preferenceGroupId] ?? emptySlot(),
          group
        ),
      },
    })),
  updatePreferenceGroupDraft: (preferenceGroupId, updater) =>
    set((state) => ({
      preferenceGroups: {
        ...state.preferenceGroups,
        [preferenceGroupId]: updateDraftSlot(
          state.preferenceGroups[preferenceGroupId] ?? emptySlot(),
          updater
        ),
      },
    })),
  commitPreferenceGroup: (preferenceGroupId, group, submittedDraft) =>
    set((state) => {
      const currentDraft = state.preferenceGroups[preferenceGroupId]?.draft
      return {
        preferenceGroups: {
          ...state.preferenceGroups,
          [preferenceGroupId]:
            submittedDraft && currentDraft
              ? {
                  saved: cloneSnapshot(group),
                  draft: rebaseDraft(submittedDraft, currentDraft, group),
                }
              : committedSlot(group),
        },
      }
    }),
  replacePreferenceGroupSlot: (preferenceGroupId, slot) =>
    set((state) => ({
      preferenceGroups: {
        ...state.preferenceGroups,
        [preferenceGroupId]: replacedSlot(slot),
      },
    })),
  discardPreferenceGroup: (preferenceGroupId) =>
    set((state) => {
      const slot = state.preferenceGroups[preferenceGroupId]
      if (!slot) return state

      return {
        preferenceGroups: {
          ...state.preferenceGroups,
          [preferenceGroupId]: discardedSlot(slot),
        },
      }
    }),

  hydrateUserPreferences: (preferences) =>
    set((state) => ({
      userPreferences: hydrateSlot(state.userPreferences, preferences),
    })),
  updateUserPreferencesDraft: (updater) =>
    set((state) => ({
      userPreferences: updateDraftSlot(state.userPreferences, updater),
    })),
  commitUserPreferences: (preferences, submittedDraft) =>
    set((state) => ({
      userPreferences:
        submittedDraft && state.userPreferences.draft
          ? {
              saved: cloneSnapshot(preferences),
              draft: rebaseDraft(submittedDraft, state.userPreferences.draft, preferences),
            }
          : committedSlot(preferences),
    })),
  replaceUserPreferencesSlot: (slot) => set({ userPreferences: replacedSlot(slot) }),
  discardUserPreferences: () =>
    set((state) => ({
      userPreferences: discardedSlot(state.userPreferences),
    })),

  resetPreferenceEditing: () =>
    set({
      preferenceDefaults: emptySlot(),
      preferenceGroups: {},
      userPreferences: emptySlot(),
    }),
}))
